/**
 * Pods API endpoints — CRUD, membership, invitations.
 * See VIZDAY_POD_SPEC.md v1.0.
 */
import { fetchApi } from './apiBase';

// === Types ===

export interface PodMemberPreview {
  id: number;
  name: string;
  initials: string;
}

export interface PodRecentActivity {
  latest_plan_title: string;
  latest_plan_date: string;
  total_plans: number;
}

export interface PodListItem {
  id: string;
  name: string;
  member_count: number;
  member_previews: PodMemberPreview[];
  my_status: 'pending' | 'active' | 'left' | 'removed' | null;
  recent_activity: PodRecentActivity | null;
  created_at: string;
  updated_at: string;
}

export interface PodMember {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'active' | 'left' | 'removed';
  joined_at: string | null;
  invited_at: string;
}

export interface PodPlan {
  plan_id: number;
  title: string;
  destination: string;
  start_date: string | null;
  end_date: string | null;
  lifecycle_state: string;
}

export interface PodStats {
  total_plans: number;
  total_days_shared: number;
}

export interface PodDetail extends PodListItem {
  members?: PodMember[];
  plans?: PodPlan[];
  stats?: PodStats;
  is_creator?: boolean;
  admin_user_id?: number | null;
}

// === API Functions ===

export async function getPods(): Promise<PodListItem[]> {
  return fetchApi<PodListItem[]>('/api/planner/pods/');
}

export async function getPodDetail(podId: string): Promise<PodDetail> {
  return fetchApi<PodDetail>(`/api/planner/pods/${podId}/`);
}

export async function createPod(
  name: string,
  inviteEmails?: string[],
): Promise<PodListItem> {
  return fetchApi<PodListItem>('/api/planner/pods/', {
    method: 'POST',
    body: JSON.stringify({
      name,
      invite_emails: inviteEmails || [],
    }),
  });
}

export async function renamePod(podId: string, name: string): Promise<PodDetail> {
  return fetchApi<PodDetail>(`/api/planner/pods/${podId}/`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function deletePod(podId: string): Promise<void> {
  await fetchApi(`/api/planner/pods/${podId}/`, { method: 'DELETE' });
}

export async function invitePodMember(podId: string, email: string): Promise<void> {
  await fetchApi(`/api/planner/pods/${podId}/invite/`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function leavePod(podId: string): Promise<void> {
  await fetchApi(`/api/planner/pods/${podId}/leave/`, { method: 'POST' });
}

export async function removePodMember(podId: string, userId: number): Promise<void> {
  await fetchApi(`/api/planner/pods/${podId}/remove/${userId}/`, { method: 'POST' });
}

export async function acceptPodInvite(podId: string): Promise<void> {
  await fetchApi(`/api/planner/pods/${podId}/accept/`, { method: 'POST' });
}
